import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, PageBody, EmptyState } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Search, Trash2, Edit2 } from "lucide-react";
import { toast } from "sonner";

const empty = {
  part_number: "", manufacturer: "", description: "",
  main_category: "", sub_category: "", sub_sub_category: "",
  gtip: "", unit_weight: "", origin: "", tax_percent: "", notes: "",
};

export default function Items() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState({ main: [], sub: [], sub_sub: [] });
  const [filters, setFilters] = useState({ main_category: "all", sub_category: "all", sub_sub_category: "all" });
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => {
    const params = { q: q || undefined };
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    return Promise.all([
      api.get("/items", { params }).then((r) => setItems(r.data)),
      api.get("/items/categories").then((r) => setCats(r.data)),
    ]);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    try {
      const uw = String(form.unit_weight ?? "").trim().replace(",", ".");
      const tx = String(form.tax_percent ?? "").trim().replace(",", ".");
      const payload = {
        ...form,
        unit_weight: uw === "" || Number.isNaN(parseFloat(uw)) ? null : parseFloat(uw),
        tax_percent: tx === "" || Number.isNaN(parseFloat(tx)) ? null : parseFloat(tx),
        origin: (form.origin || "").trim() || null,
      };
      if (editId) {
        await api.put(`/items/${editId}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/items", payload);
        toast.success("Item added");
      }
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch { toast.error("Failed"); }
  };

  const startEdit = (it) => { setForm({ ...empty, ...it }); setEditId(it.id); setOpen(true); };
  const startNew = () => { setForm(empty); setEditId(null); setOpen(true); };

  const del = async (iid) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/items/${iid}`);
    toast.success("Deleted");
    load();
  };

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <>
      <PageHeader
        title="Items"
        subtitle="Catalog"
        testid="items-title"
        actions={
          <Button className="rounded-none bg-blue-600 hover:bg-blue-700 text-white" onClick={startNew} data-testid="new-item-btn">
            <Plus className="w-4 h-4 mr-2" /> New Item
          </Button>
        }
      />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setForm(empty); } }}>
        <DialogContent className="rounded-none max-w-xl">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editId ? "Edit item" : "New item"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-caps">Part number *</Label>
                <Input required className="rounded-none mt-1" value={form.part_number} onChange={(e) => setForm({ ...form, part_number: e.target.value })} data-testid="item-part-number" /></div>
              <div><Label className="label-caps">Manufacturer</Label>
                <Input className="rounded-none mt-1" value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} data-testid="item-manufacturer" /></div>
            </div>
            <div>
              <Label className="label-caps">Description</Label>
              <Textarea rows={2} className="rounded-none mt-1" placeholder="e.g. MOSFET N-CH 60V 190MA TO236AB" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="item-description" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="label-caps">Main category</Label>
                <Input list="cats-main" className="rounded-none mt-1" placeholder="Discrete Semiconductor" value={form.main_category} onChange={(e) => setForm({ ...form, main_category: e.target.value })} data-testid="item-main-cat" />
                <datalist id="cats-main">{cats.main.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
              <div><Label className="label-caps">Sub category</Label>
                <Input list="cats-sub" className="rounded-none mt-1" placeholder="Transistor" value={form.sub_category} onChange={(e) => setForm({ ...form, sub_category: e.target.value })} data-testid="item-sub-cat" />
                <datalist id="cats-sub">{cats.sub.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
              <div><Label className="label-caps">Sub-sub category</Label>
                <Input list="cats-subsub" className="rounded-none mt-1" placeholder="Mosfet" value={form.sub_sub_category} onChange={(e) => setForm({ ...form, sub_sub_category: e.target.value })} data-testid="item-subsub-cat" />
                <datalist id="cats-subsub">{cats.sub_sub.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
            </div>
            <div>
              <Label className="label-caps">GTIP kodu</Label>
              <Input className="rounded-none mt-1 font-mono" placeholder="örn. 8542.31.90.00" value={form.gtip} onChange={(e) => setForm({ ...form, gtip: e.target.value })} data-testid="item-gtip" />
              <div className="text-[10px] text-slate-500 mt-1">Gümrük tarife kodu — yükleme kalemlerine otomatik taşınır</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-caps">Birim ağırlık (kg)</Label>
                <Input className="rounded-none mt-1 font-mono" type="text" inputMode="decimal" placeholder="örn. 0.012"
                  value={form.unit_weight ?? ""} onChange={(e) => setForm({ ...form, unit_weight: e.target.value })} data-testid="item-unit-weight" />
                <div className="text-[10px] text-slate-500 mt-1">Packing listte toplam ağırlık = adet × birim ağırlık</div>
              </div>
              <div>
                <Label className="label-caps">Menşei</Label>
                <Input className="rounded-none mt-1" placeholder="örn. CN, MY, DE" value={form.origin ?? ""} onChange={(e) => setForm({ ...form, origin: e.target.value })} data-testid="item-origin" />
              </div>
            </div>
            <div>
              <Label className="label-caps">Vergi/Harç % (varsa)</Label>
              <Input className="rounded-none mt-1 font-mono" type="text" inputMode="decimal" placeholder="örn. 20 (= %20)"
                value={form.tax_percent ?? ""} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} data-testid="item-tax-percent" />
              <div className="text-[10px] text-slate-500 mt-1">Yüklemede maliyet = adet × alış × (1 + vergi/100). Boş = vergisiz</div>
            </div>
            <div>
              <Label className="label-caps">Notes</Label>
              <Textarea rows={2} className="rounded-none mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="item-notes" />
            </div>
            <DialogFooter><Button type="submit" className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="item-save-btn">{editId ? "Save changes" : "Add item"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <PageBody>
        {/* Filters */}
        <div className="grid md:grid-cols-4 gap-3 mb-4 items-end">
          <div>
            <Label className="label-caps">Search</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Part, manufacturer, description…" className="rounded-none mt-1 pl-9" data-testid="items-search" />
            </div>
          </div>
          <div>
            <Label className="label-caps">Main category</Label>
            <Select value={filters.main_category} onValueChange={(v) => setF("main_category", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="filter-main-cat"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All</SelectItem>
                {cats.main.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Sub category</Label>
            <Select value={filters.sub_category} onValueChange={(v) => setF("sub_category", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="filter-sub-cat"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All</SelectItem>
                {cats.sub.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-caps">Sub-sub category</Label>
            <Select value={filters.sub_sub_category} onValueChange={(v) => setF("sub_sub_category", v)}>
              <SelectTrigger className="rounded-none mt-1" data-testid="filter-subsub-cat"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All</SelectItem>
                {cats.sub_sub.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {items.length === 0 ? <EmptyState title="No items yet" hint="Add your first item to build the catalog." action={<Button onClick={startNew} className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="empty-new-item-btn"><Plus className="w-4 h-4 mr-2" /> New Item</Button>} /> :
          <div className="bg-white border border-slate-300 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-300">
                <tr className="text-left">
                  <th className="py-2 px-3 label-caps">Part #</th>
                  <th className="py-2 px-3 label-caps">Mfr</th>
                  <th className="py-2 px-3 label-caps">Description</th>
                  <th className="py-2 px-3 label-caps">GTIP</th>
                  <th className="py-2 px-3 label-caps">Category</th>
                  <th className="py-2 px-3 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-slate-200 hover:bg-slate-50 stagger-row" data-testid={`item-row-${it.id}`}>
                    <td className="py-2 px-3 font-mono font-medium">{it.part_number}</td>
                    <td className="py-2 px-3">{it.manufacturer || "-"}</td>
                    <td className="py-2 px-3 text-slate-600 max-w-md">{it.description || "-"}</td>
                    <td className="py-2 px-3 font-mono text-xs">{it.gtip || <span className="text-slate-400">—</span>}</td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1 text-xs">
                        {it.main_category && <span className="font-mono bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-700/10 px-1.5 py-0.5 rounded-sm">{it.main_category}</span>}
                        {it.sub_category && <><span className="text-slate-400">›</span><span className="font-mono bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-700/10 px-1.5 py-0.5 rounded-sm">{it.sub_category}</span></>}
                        {it.sub_sub_category && <><span className="text-slate-400">›</span><span className="font-mono bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-700/10 px-1.5 py-0.5 rounded-sm">{it.sub_sub_category}</span></>}
                        {!it.main_category && !it.sub_category && !it.sub_sub_category && <span className="text-slate-400">—</span>}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <div className="inline-flex gap-2">
                        <button className="text-slate-400 hover:text-blue-700" onClick={() => startEdit(it)} data-testid={`item-edit-${it.id}`}><Edit2 className="w-4 h-4" /></button>
                        <button className="text-slate-400 hover:text-red-600" onClick={() => del(it.id)} data-testid={`item-del-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </PageBody>
    </>
  );
}
